import React, { createContext, useEffect, useState } from "react";

import { apiFetch, apiOrderCheckoutFetch } from "../apiBindings";
import { navigate } from "../components/shared/LinkComponent";
import usePrevious from "../hooks/usePrevious";
import pushToAnalytics from "../utils/analytics";
import {
    storageLocal as storage,
    storageSession as session,
} from "../utils/StorageHandler";
import checkoutSteps from "./ElectricityCheckoutSteps";

export const DataContext = createContext();

const ElectricityCheckoutDataProvider = ({ checkoutStep, children }) => {
    /**
     * ###################################################################
     * ini vars and state
     * ###################################################################
     */

    const baseUrl = "/strom/bestellung/";
    const steps = 3;
    const [loading, setLoading] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [editMode, setEditMode] = useState(false);
    const [productChanged, setProductChanged] = useState(0);
    const [detailsChanged, setDetailsChanged] = useState(0);

    const [product, setProduct] = useState();

    const [paymentTypeChanged, setPaymentTypeChanged] = useState(0);
    const [stepButtonAction, setStepButtonAction] = useState(null);
    const [overlayContent, setOverlayContent] = useState(null);
    const [checkoutData, setCheckoutData] = useState(
        session.getJson("ele:checkout")
    );
    const [orderData, setOrderData] = useState(
        session.getJson("ele:order"),
        null
    );
    const [verbandsnummer] = useState(storage.get("ele:verbandsnummer") || "");
    const [tariffCode] = useState(storage.get("ele:tariff-code") || "");
    const [tariffType] = useState(storage.getInt("ele:tariff-type"));
    const [tariffId] = useState(storage.getInt("ele:tariff-id"));

    const zipcode = storage.get("ele:zipcode");
    const [consumption, setConsumption] = useState(
        storage.get("ele:consumption")
    );
    const [street] = useState(storage.get("ele:street-select-list-selected"));
    const [houseNum] = useState(storage.get("ele:house-number"));
    const [place] = useState(storage.get("ele:place-select-list-selected"));
    const previousStepIndex = usePrevious(currentStepIndex);

    /**
     * Data for the delivery address step.
     */
    const [deliveryAddressData, setDeliveryAddressData] = useState(
        session.getJson("ele:delivery-address", {
            company: "",
            salutation: "Herr",
            firstname: "",
            lastname: "",
            birthdate: "",
            email: "",
            phone: "",
        })
    );

    const [invoiceAddress, setInvoiceAddress] = useState(
        storage.getJson("ele:invoice-address", {
            invoice_company: "",
            invoice_salutation: "Herr",
            invoice_firstname: "",
            invoice_lastname: "",
            invoice_email: "",
            invoice_phone: "",
            invoice_street: "",
            invoice_zipcode: "",
            invoice_city: "",
        })
    );

    const [deliveryDetails, setDeliveryDetails] = useState(
        session.getJson("ele:delivery-details", {
            delivery_movement_type: "Umzug/Einzug",
            delivery_counter_number: "",
            delivery_previous_supplier: "",
            delivery_change_date_immediately: "nächstmöglich",
            delivery_change_date: "",
            delivery_supplier_terminated: "0",
            delivery_supplier_terminated_date: new Date(
                new Date().setDate(new Date().getDate() + 14)
            )
                .toISOString()
                .slice(0, 10),
            promotion_code: "",
        })
    );

    const paymentTypes = [
        { id: 1, name: "Sepa Lastschriftmandat", addition: 0 },
        { id: 2, name: "Überweisung", addition: 0 },
    ];
    const [paymentData, setPaymentData] = useState(
        storage.getJson("ele:payment-data", {
            payment_payment_type: 1,
            payment_account_holder: "",
            payment_bank_name: "",
            payment_sepa_direct_granted: false,
            payment_different_account_holder: false,
            payment_salutation: "Herr",
            payment_firstname: "",
            payment_lastname: "",
            payment_email: "",
            payment_phone: "",
            payment_street: "",
            payment_zipcode: "",
            payment_city: "",
        })
    );
    const [bic, setBic] = useState("");
    const [iban, setIban] = useState("");
    const [useInvoiceAddress, setUseInvoiceAddress] = useState(
        storage.getInt("ele:use-invoice-address", 0)
    );
    const [orderComment, setOrderComment] = useState(
        storage.get("ele:order-comment", "")
    );

    /**
     * Current chosen payment type
     */
    const [paymentType, setPaymentType] = useState(
        storage.getInt("ele:payment-type", paymentTypes[0].id)
    );

    /**
     * AGB checked
     */
    const [agbChecked, setAgbChecked] = useState(false);

    /**
     * The current price we have.
     */
    const [price, setPrice] = useState({
        monthly_charge: 0,
        unit_price: 0,
        price_total_gross: 0,
    });

    /**
     * Form validation errors.
     */
    const [formErrors, setFormErrors] = useState([]);

    const [nextButtonDisabled, setNextButtonDisabled] = useState(true);

    /**
     *  Receive Newsletter
     */
    const [receiveNewsletter, setReceiveNewsletter] = useState(
        storage.getInt("ele:receive-newsletter")
            ? storage.getInt("ele:receive-newsletter")
            : 0
    );

    /**
     * ###################################################################
     * Vars: set useEffects
     * ###################################################################
     */

    /**
     * When the user changes the payment type in the overlay.
     * Will be saved without pushing the save button.
     */
    useEffect(() => {
        if (overlayContent === "zahlart") {
            storage.set("ele:gtag:current-value", paymentType);
        }
        storage.set("ele:payment-type", paymentType);
    }, [paymentType, overlayContent]);

    /**
     * When the user accepts the agb.
     */
    useEffect(() => {
        if (agbChecked) {
            clearFormError("agb");
        }
    }, [agbChecked]);

    /**
     * When the user change acceptance of Newsletter
     */
    useEffect(() => {
        storage.set("ele:receive-newsletter", receiveNewsletter);
    }, [receiveNewsletter]);

    /**
     * When the user changes useInvoiceAddress.
     */
    useEffect(() => {
        storage.set("ele:use-invoice-address", useInvoiceAddress);
    }, [useInvoiceAddress]);

    /**
     * When the user changes the consumption in the overlay.
     * Will be saved without pushing the save button.
     */
    useEffect(() => {
        if (overlayContent === "consumption") {
            storage.set("ele:gtag:current-value", consumption);
        }
    }, [consumption, overlayContent]);

    /**
     * When the user changes the comment in the overlay.
     * Will be saved without pushing the save button.
     */
    useEffect(() => {
        if (overlayContent === "lieferdetails") {
            storage.set("ele:gtag:current-value", orderComment);
        }
    }, [orderComment, overlayContent]);

    /**
     * Handle the current step index.
     */
    useEffect(() => {
        // Init the checkout, if not exists.
        if (session.get("ele:checkout") === null) {
            initCheckout();
        }

        // If we leave the delivery address step do something with it
        if (checkoutSteps[previousStepIndex]) {
            const previousUrl = checkoutSteps[previousStepIndex].url;

            if (previousUrl === "lieferadresse") {
                if (stepButtonAction === "next") {
                    storeDeliveryAddressToStorage();
                    storeInvoiceAddressToStorage();
                    // Store to server
                    storeDeliveryAddressToServer();
                }
            }
            if (previousUrl === "zahlart") {
                if (stepButtonAction === "next") {
                    storePaymentTypeToStorage();
                    // Store to server
                    storePaymentToServer();
                }
            }
            if (previousUrl === "lieferdetails") {
                if (stepButtonAction === "next") {
                    storeOrderCommentToStorage();
                    storeDeliveryDetailsToStorage();
                    storeDeliveryDetailsToServer();
                    // storePaymentDetailsToServer();
                    storePaymentToStorage();

                    // Store to server
                } else {
                    setOrderCommentStorageToState();
                    storePaymentToStorage();
                    storePaymentToServer();
                    storePaymentToStorage();
                }
            }
            if (stepButtonAction === "next" && previousUrl === "uebersicht") {
                if (session.getJson("ele:order") === null) {
                    sendOrderToServer();
                }
            }
        }

        setCurrentStepIndex(
            checkoutSteps.findIndex((step) => step.url === checkoutStep)
        );
        setStepButtonAction(null);
    }, [checkoutStep]);

    /**
     * When the details changed (state) the price should be updated.
     */
    useEffect(() => {
        if (detailsChanged > 0) {
            storeDeliveryDetailsToServer(true);
        }
    }, [detailsChanged]);

    /**
     * When the payment type changed (state) the price should be updated.
     */
    useEffect(() => {
        if (paymentTypeChanged > 0) {
            storePaymentToServer(true);
        }
    }, [paymentTypeChanged]);

    useEffect(() => {
        if (typeof checkoutData != "undefined" && checkoutData != null) {
            setProduct(
                Object.values(checkoutData).find(
                    (product) => product.tarifID == tariffId
                )
            );
        }
    }, [checkoutData]);

    useEffect(() => {
        if (checkoutData && checkoutData.user_hash) {
            setNextButtonDisabled(false);
        } else {
            setNextButtonDisabled(true);
        }
    }, [checkoutData]);

    /**
     * When the save button in the overlay is pressed.
     */
    const overlaySave = () => {
        if (overlayContent === "zahlart") {
            storePaymentTypeToStorage();
            // Store to server
            storePaymentToServer();
        }
        if (overlayContent === "lieferdetails") {
            storeOrderCommentToStorage();
            //storeDeliveryTimeToStorage();

            // Store to server
            storeDeliveryDetailsToServer();
        }
        if (overlayContent === "lieferadresse") {
            storeDeliveryAddressToStorage();
            // Store to server
            storeDeliveryAddressToServer();
        }
        if (overlayContent === "consumption") {
            storeConsumptionToStorage();
        }
        setOverlayContent(null);
    };

    /**
     * When the back button in the overlay is pressed.
     */
    const overlayBack = () => {
        if (overlayContent === "zahlart") {
            setPaymentTypeStorageToState();
            paymentTypeHasChanged();
        }
        if (overlayContent === "lieferdetails") {
            setOrderCommentStorageToState();
            detailsHasChanged();
        }
        if (overlayContent === "lieferadresse") {
            setDeliveryAddressStorageToState();
        }
        if (overlayContent === "menge") {
            setConsumptionStorageToState();
        }
        setOverlayContent(null);
    };

    /**
     * Check if the button for the previous step should be visible.
     */
    const previousStepVisible = () => {
        if (
            (currentStepIndex === 1 &&
                storage.getBoolean("ele:product-is-premium")) ||
            currentStepIndex === 0
        ) {
            return false;
        }
        return true;
    };

    /**
     * ###################################################################
     * Vars: state => localStorage
     * ###################################################################
     */

    /**
     * Stores the consumption the storage.
     */
    const storeConsumptionToStorage = () => {
        storage.set("ele:consumption", consumption);
    };

    /**
     * Stores the delivery address in the storage.
     */
    const storeDeliveryAddressToStorage = () => {
        session.setJson("ele:delivery-address", deliveryAddressData);
    };

    /**
     * Stores the invoice informations to storage.
     */
    const storeInvoiceAddressToStorage = () => {
        storage.setJson("ele:invoice-address", invoiceAddress);
        storage.set("ele:use-invoice-address", useInvoiceAddress);
    };

    /**
     * Store the payment type in the storage.
     */
    const storePaymentTypeToStorage = () => {
        storage.set("ele:payment-type", paymentType);
    };

    /**
     * Store the payment type in the storage.
     */
    const storeOrderCommentToStorage = () => {
        storage.set("ele:order-comment", orderComment);
    };

    /**
     * Stores Delivery Details to the storage.
     */
    const storeDeliveryDetailsToStorage = () => {
        session.setJson("ele:delivery-details", deliveryDetails);
    };

    /**
     * Store payment to storage
     */
    const storePaymentToStorage = () => {
        storage.setJson("ele:payment-data", paymentData);
    };

    /**
     * ###################################################################
     * Vars: localStorage => state.
     * ###################################################################
     */

    /**
     * Sets the consumption from storage to state.
     */
    const setConsumptionStorageToState = () => {
        setConsumption(storage.get("ele:consumption"));
    };

    /**
     * Sets the delivery address from storage to the state.
     */
    const setDeliveryAddressStorageToState = () => {
        setDeliveryAddressData(session.getJson("ele:delivery-address"));
    };

    /**
     * Set the payment type from storage to the state.
     */
    const setPaymentTypeStorageToState = () => {
        setPaymentType(storage.getInt("ele:payment-type"));
    };

    /**
     * Set the payment type from storage to the state.
     */
    const setOrderCommentStorageToState = () => {
        setOrderComment(storage.get("ele:order-comment", ""));
    };

    /**
     * ###################################################################
     * funky stuff
     * ###################################################################
     */

    /**
     * Returns the current payment type.
     */
    const getCurrentPaymentType = () => {
        return paymentTypes.find((item) => item.id == paymentType);
    };

    /**
     * Returns the current prices.
     */
    const getPrices = () => {
        if (!product) {
            return null;
        }

        return {
            consumption: consumption,
            grundpreisBrutto: product.grundpreisBrutto,
            arbeitspreisHTCtKWhBrutto: product.arbeitspreisHTCtKWhBrutto,
            gesamtpreisBruttoProMonat: product.gesamtpreisBruttoProMonat,
            enegieMix: Math.round(product.energyMix.erneuerbare),
            gesamtpreisOhneBoniBrutto: product.gesamtpreisOhneBoniBrutto,
            tarifName: product.tarifName,
        };
    };

    /**
     * Indicator for product has changed.
     */
    const productHasChanged = () => {
        setProductChanged(productChanged + 1);
    };

    /**
     * Indicator for details has changed.
     */
    const detailsHasChanged = () => {
        setDetailsChanged(detailsChanged + 1);
    };

    /**
     * Indicator for payment type has changed.
     */
    const paymentTypeHasChanged = () => {
        setPaymentTypeChanged(paymentTypeChanged + 1);
    };

    /**
     * ###################################################################
     * Navigation
     * ###################################################################
     */

    /**
     * Go to the next step and adjust the router.
     */
    const nextStep = () => {
        if (currentStepIndex + 1 < checkoutSteps.length) {
            const currentStep = checkoutSteps[currentStepIndex].url;
            const nextStep = baseUrl + checkoutSteps[currentStepIndex + 1].url;
            if (currentStep === "lieferadresse") {
                if (!deliveryAddressStepIsValid()) {
                    return;
                }
            } else if (currentStep === "zahlart") {
                if (
                    paymentData.payment_payment_type === 1 &&
                    !paymentStepIsValid()
                ) {
                    return;
                }
                if (!paymentInvoiceIsValid()) {
                    return;
                }
            } else if (currentStep === "lieferdetails") {
                if (!deliveryDetailsIsValid()) {
                    return;
                }
            } else if (currentStep === "uebersicht") {
                if (!agbChecked) {
                    addFormError("agb");
                    return;
                }
            }
            if (formErrors.length === 0) {
                navigate(nextStep);
            }
        }
    };

    /**
     * Go to the previous step and adjust the router.
     */
    const previousStep = () => {
        setFormErrors([]);
        navigate(baseUrl + checkoutSteps[currentStepIndex - 1].url);
    };

    /**
     * ###################################################################
     * Validation.
     * ###################################################################
     */

    /**
     * Check if the delivery address step is valid.
     */
    const deliveryAddressStepIsValid = () => {
        let errors = [...formErrors];
        let index = null;

        errors = Array.from(new Set(errors));

        // Check if the fields are filled for delivery address
        if (deliveryAddressData.firstname === "") {
            errors.push("firstname");
        } else {
            index = errors.indexOf("firstname");
            errors.splice(index, 1);
        }
        if (deliveryAddressData.lastname === "") {
            errors.push("lastname");
        }
        if (deliveryAddressData.birthdate === "") {
            errors.push("birthdate");
        }
        if (deliveryAddressData.phone === "") {
            errors.push("phone");
        }
        if (deliveryAddressData.email === "") {
            errors.push("email");
        }
        if (deliveryAddressData.street === "") {
            errors.push("street");
        }
        if (deliveryAddressData.city === "") {
            errors.push("city");
        }
        // Check if the fields are filled for invoice address
        if (useInvoiceAddress) {
            if (invoiceAddress.invoice_firstname === "") {
                errors.push("invoice_firstname");
            }
            if (invoiceAddress.invoice_lastname === "") {
                errors.push("invoice_lastname");
            }
            if (invoiceAddress.invoice_street === "") {
                errors.push("invoice_street");
            }
            if (invoiceAddress.invoice_zipcode === "") {
                errors.push("invoice_zipcode");
            }
            if (invoiceAddress.invoice_city === "") {
                errors.push("invoice_city");
            }
            if (invoiceAddress.invoice_email === "") {
                errors.push("invoice_email");
            }
            if (invoiceAddress.invoice_phone === "") {
                errors.push("invoice_phone");
            }
        } else {
            errors = Array.from(new Set(errors));
            errors = errors.filter((error) => !error.startsWith("inv"));
        }

        errors = Array.from(new Set(errors));
        setFormErrors(errors);

        if (errors.length) {
            return false;
        }
        return true;
    };

    /**
     * Validate delivery details
     */

    const deliveryDetailsIsValid = () => {
        let errors = [...formErrors];
        errors = errors.filter((error) => !error.startsWith("deliveryDetails"));

        if (deliveryDetails.delivery_movement_type === "Umzug/Einzug") {
            if (deliveryDetails.delivery_change_date === "") {
                errors.push("delivery_change_date");
            }
        }

        if (deliveryDetails.delivery_movement_type === "Lieferantenwechsel") {
            if (
                deliveryDetails.delivery_change_date_immediately ===
                    "frühestens" &&
                deliveryDetails.delivery_supplier_terminated_date === ""
            ) {
                errors.push("delivery_supplier_terminated_date");
            }
            if (deliveryDetails.delivery_previous_supplier === "") {
                errors.push("delivery_previous_supplier");
            }
        }
        if (deliveryDetails.delivery_counter_number === "") {
            errors.push("delivery_counter_number");
        }
        if (
            deliveryDetails.delivery_supplier_terminated == "1" &&
            deliveryDetails.delivery_supplier_terminated_date === ""
        ) {
            errors.push("delivery_supplier_terminated_date");
        }

        setFormErrors(errors);

        if (errors.length > 0) {
            return false;
        }

        return true;
    };

    /**
     * Check if Payment Step is valid
     */

    const paymentStepIsValid = () => {
        let errors = [...formErrors];
        if (paymentData.payment_account_holder === "") {
            errors.push("payment_account_holder");
        }
        if (paymentData.payment_bank_name === "") {
            errors.push("payment_bank_name");
        }

        if (iban === "") {
            errors.push("iban");
        }
        if (!paymentData.payment_sepa_direct_granted) {
            errors.push("payment_sepa_direct_granted");
        }

        setFormErrors(errors);

        if (errors.length > 0) {
            return false;
        }
        return true;
    };

    /**
     * if contact and delivery address for different user details is valid
     */

    const paymentInvoiceIsValid = () => {
        let errors = [...formErrors];
        if (paymentData.payment_different_account_holder === true) {
            if (paymentData.payment_firstname === "") {
                errors.push("payment_firstname");
            }
            if (paymentData.payment_lastname === "") {
                errors.push("payment_lastname");
            }

            if (paymentData.payment_email === "") {
                errors.push("payment_email");
            }
            if (paymentData.payment_phone === "") {
                errors.push("payment_phone");
            }
            if (paymentData.payment_street === "") {
                errors.push("payment_street");
            }
            if (paymentData.payment_zipcode === "") {
                errors.push("payment_zipcode");
            }
            if (paymentData.payment_city === "") {
                errors.push("payment_city");
            }
        } else {
            errors = errors.filter((error) => !error.startsWith("payment"));
        }

        setFormErrors(errors);

        if (errors.length > 0) {
            return false;
        }

        return true;
    };

    /**
     * Add an error to the errors.
     *
     * @param {*} key
     */
    const addFormError = (key) => {
        setFormErrors([...formErrors, key]);
    };

    /**
     * Remove an element from the errors.
     *
     * @param {*} key
     */
    const clearFormError = (key) => {
        setFormErrors(formErrors.filter((item) => item !== key));
    };

    /**
     * Check if the given key is present in the errors.
     *
     * @param {*} key
     */
    const hasFormErrorAt = (key) => {
        return formErrors.filter((item) => item === key).length > 0;
    };

    /**
     * ###################################################################
     * API
     * ###################################################################
     */

    /**
     * Send a request to the api.
     *
     * @param {*} endpoint
     * @param {*} data
     * @param {*} onSuccess
     */
    const apiRequest = (
        endpoint,
        data,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSuccess = () => {}
    ) => {
        setLoading(true);
        apiOrderCheckoutFetch(`/electricity/checkout/${endpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: JSON.stringify(data),
        })
            .then((response) => response.json())
            .then((data) => {
                onSuccess(data);
                setLoading(false);
            });
    };

    /**
     * Store the delivery address data to the server.
     */
    const storeDeliveryAddressToServer = () => {
        const data = {
            hash: checkoutData.user_hash,
            deliveryAddress: deliveryAddressData,
            street: street,
            city: place,
            zipcode: zipcode,
            houseNum: houseNum,
            useInvoiceAddress: useInvoiceAddress,
            invoiceAddress: invoiceAddress,
        };
        apiRequest("deliveryAddress", data);
    };

    /**
     * Init the checkout process.
     */
    const initCheckout = async () => {
        const response = await apiFetch(
            `/electricity/checkout/init/${zipcode}/${consumption}/${verbandsnummer}/${tariffCode}/${tariffType}/${tariffId}/1`
        );
        const data = await response.json();
        session.setJson("ele:checkout", data);
        setCheckoutData(data);
        setDeliveryAddressData({
            ...deliveryAddressData,
            ...{ zipcode: zipcode },
        });

        return data;
    };

    /**
     * Store the delivery details to the server.
     */
    const storeDeliveryDetailsToServer = () => {
        const data = {
            hash: checkoutData.user_hash,
            deliveryDetails: { ...deliveryDetails },
        };
        const onSuccess = (data) => {
            const newData = { ...checkoutData, ...data };
            session.setJson("ele:checkout", newData);
            setCheckoutData(newData);
        };

        apiRequest("deliveryDetails", data, onSuccess);
    };

    /**
     * Store the payment type to the server.

     const storePaymentTypeToServer = (calculateOnly = false) => {
        const data = {
            hash: checkoutData.user_hash,
            paymentType,
            ...(calculateOnly ? {calculateOnly} : {}),
        };
        const onSuccess = (data) => {
            const newData = {...checkoutData, ...data};
            session.setJson("ele:checkout", newData);
            setCheckoutData(newData);
        };

        apiRequest("paymentType", data, onSuccess);
    };
     */
    /**
     * Store the payment details to the server.
     */
    const storePaymentToServer = () => {
        apiFetch("/electricity/checkout/updatePayment", {
            method: "post",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: JSON.stringify({
                hash: checkoutData.user_hash,
                payment: { ...paymentData },
                bic: bic,
                iban: iban,
            }),
        });
    };

    /**
     * Creates an order from the checkout
     */
    const sendOrderToServer = () => {
        const data = {
            hash: checkoutData.user_hash,
            receive_newsletter: receiveNewsletter,
        };
        apiRequest("finish", data, (data) => {
            session.setJson("ele:order", data);
            pushToAnalytics("order", {
                orderNumber: data.order_number,
            });
            setOrderData(data);
        });
    };

    /**
     * ###################################################################
     * export vars, actions
     * ###################################################################
     */

    const data = {
        product,
        steps,
        checkoutSteps,
        currentStepIndex,
        deliveryDetails,
        deliveryAddressData,
        invoiceAddress,
        useInvoiceAddress,
        paymentData,
        paymentTypes,
        paymentType,
        price,
        zipcode,
        street,
        houseNum,
        place,
        consumption,
        editMode,
        orderComment,
        overlayContent,
        checkoutData,
        orderData,
        loading,
        agbChecked,
        formErrors,
        tariffId,
        nextButtonDisabled,
        receiveNewsletter,
        iban,
        bic,
        verbandsnummer,
    };

    const actions = {
        setCurrentStepIndex,
        setDeliveryAddressData,
        setInvoiceAddress,
        setUseInvoiceAddress,
        setDeliveryDetails,
        setPaymentData,
        getCurrentPaymentType,
        paymentTypeHasChanged,
        setBic,
        setIban,
        nextStep,
        setConsumption,
        previousStep,
        setPrice,
        setEditMode,
        setStepButtonAction,
        setOrderComment,
        previousStepVisible,
        setOverlayContent,
        overlayBack,
        overlaySave,
        getPrices,
        detailsHasChanged,
        productHasChanged,
        setAgbChecked,
        setReceiveNewsletter,
        addFormError,
        clearFormError,
        setFormErrors,
        hasFormErrorAt,
        deliveryDetailsIsValid,
    };

    return (
        <DataContext.Provider
            value={{
                data,
                actions,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export default ElectricityCheckoutDataProvider;
